// app/javascript/controllers/gallery_controller.js
import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

export default class extends Controller {
  static targets = ["modal", "modalImage", "spinner", "caption", "captionBackdrop"]
  static values = { albumTitle: String }

  connect() {
    if (this.hasModalTarget) {
      this.modalInstance = new bootstrap.Modal(this.modalTarget)
      this._bindModalKeyboard()
    }
    this._collectImages()
    this.currentIndex = 0
    this._bindTouch()
    this._preloadCache = new Set()
  }

  disconnect() {
    this._unbindModalKeyboard()
    this._unbindTouch()
  }

  _collectImages() {
    const els = Array.from(this.element.querySelectorAll("[data-gallery-url]"))
    this._imageEls = els
    this.images = els.map(el => el.getAttribute("data-gallery-url")).filter(Boolean)
  }

  openFromCard(event) {
    event.preventDefault()
    this._collectImages()
    if (!this.images || this.images.length === 0) {
      return
    }
    this.currentIndex = 0
    this._showCurrent()
  }

  cardKeydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.openFromCard(event)
    }
  }

  openFromThumb(event) {
    event.preventDefault()
    this._collectImages()
    const btn = event.currentTarget
    const idx = parseInt(btn.dataset.galleryIndexParam || btn.dataset.galleryIndex || "0", 10)
    if (Number.isNaN(idx)) return
    // If a main image is present, it's collected first in this.images.
    // Thumbnails provide a 0-based index among thumbnails only, so we offset by main count (0 or 1).
    const offset = this._mainImageCount()
    this.currentIndex = Math.min(idx + offset, (this.images?.length || 1) - 1)
    this._showCurrent()
  }

  next(event) {
    if (event) event.preventDefault()
    if (!this.images || this.images.length === 0) return
    this.currentIndex = (this.currentIndex + 1) % this.images.length
    this._renderImage()
  }

  prev(event) {
    if (event) event.preventDefault()
    if (!this.images || this.images.length === 0) return
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length
    this._renderImage()
  }

  _showCurrent() {
    this._renderImage()
    if (this.modalInstance) this.modalInstance.show()
    // Start preloading adjacent images immediately when modal opens
    this._preloadAdjacent()
    // counter is integrated into caption
  }

  _renderImage() {
    if (!this.hasModalImageTarget) return
    const url = this.images[this.currentIndex]
    // Show spinner while loading
    this._setSpinnerVisible(true)
    // Clear previous listeners to avoid stacking
    this._unbindImageLoadHandlers()
    this._onImageLoad = () => {
      this._setSpinnerVisible(false)
      // Preload adjacent images after current one loads
      this._preloadAdjacent()
    }
    this._onImageError = () => this._setSpinnerVisible(false)
    this.modalImageTarget.addEventListener('load', this._onImageLoad, { once: true })
    this.modalImageTarget.addEventListener('error', this._onImageError, { once: true })
    this.modalImageTarget.src = url
    this._updateCaption()
  }

  // Preload the next and previous images so navigation feels instant
  _preloadAdjacent() {
    if (!this.images || this.images.length <= 1) return
    const len = this.images.length
    const toPreload = [
      (this.currentIndex + 1) % len,
      (this.currentIndex - 1 + len) % len,
      (this.currentIndex + 2) % len // also preload one more ahead
    ]
    toPreload.forEach(idx => {
      const url = this.images[idx]
      if (url && !this._preloadCache.has(url)) {
        this._preloadCache.add(url)
        const img = new Image()
        img.src = url
      }
    })
  }

  _setSpinnerVisible(visible) {
    if (!this.hasSpinnerTarget) return
    this.spinnerTarget.classList.toggle('d-none', !visible)
  }

  _unbindImageLoadHandlers() {
    if (!this.hasModalImageTarget) return
    if (this._onImageLoad) this.modalImageTarget.removeEventListener('load', this._onImageLoad)
    if (this._onImageError) this.modalImageTarget.removeEventListener('error', this._onImageError)
    this._onImageLoad = null
    this._onImageError = null
  }

  _bindTouch() {
    this._touch = { startX: 0, startY: 0, active: false }
    this._onTouchStart = (e) => {
      if (!this.hasModalImageTarget) return
      const t = e.touches && e.touches[0]
      if (!t) return
      this._touch.startX = t.clientX
      this._touch.startY = t.clientY
      this._touch.active = true
    }
    this._onTouchEnd = (e) => {
      if (!this._touch.active) return
      const t = e.changedTouches && e.changedTouches[0]
      if (!t) return
      const dx = t.clientX - this._touch.startX
      const dy = t.clientY - this._touch.startY
      this._touch.active = false
      // Horizontal swipe threshold, ignore large vertical movement
      if (Math.abs(dx) > 40 && Math.abs(dy) < 80) {
        if (dx < 0) this.next()
        else this.prev()
      }
    }
    if (this.hasModalImageTarget) {
      this.modalImageTarget.addEventListener('touchstart', this._onTouchStart, { passive: true })
      this.modalImageTarget.addEventListener('touchend', this._onTouchEnd, { passive: true })
    }
  }

  _unbindTouch() {
    if (this.hasModalImageTarget) {
      if (this._onTouchStart) this.modalImageTarget.removeEventListener('touchstart', this._onTouchStart)
      if (this._onTouchEnd) this.modalImageTarget.removeEventListener('touchend', this._onTouchEnd)
    }
    this._onTouchStart = null
    this._onTouchEnd = null
    this._touch = null
  }

  _bindModalKeyboard() {
    this._onKeydown = (e) => {
      if (document.body.classList.contains('modal-open')) {
        if (e.key === 'ArrowRight') this.next(e)
        if (e.key === 'ArrowLeft') this.prev(e)
      }
    }
    document.addEventListener('keydown', this._onKeydown)
  }

  _unbindModalKeyboard() {
    if (this._onKeydown) {
      document.removeEventListener('keydown', this._onKeydown)
      this._onKeydown = null
    }
  }

  _updateCaption() {
    if (!this.hasCaptionTarget || !this._imageEls || this._imageEls.length === 0) return
    const el = this._imageEls[this.currentIndex]
    const total = this.images?.length || 0
    const idx = (this.currentIndex ?? 0) + 1
    const baseCaption = el?.getAttribute('data-gallery-caption') || ''
    const albumTitle = (this.hasAlbumTitleValue && this.albumTitleValue) ? this.albumTitleValue : ''
    // Format: Album Title (idx / N) — fallback to base caption if no album title
    const nice = albumTitle ? `${albumTitle} (${idx} / ${total})` : `${baseCaption}`
    const hasText = nice.trim().length > 0
    this.captionTarget.textContent = nice
    this.captionTarget.classList.toggle('d-none', !hasText)
    if (this.hasCaptionBackdropTarget) {
      this.captionBackdropTarget.classList.toggle('d-none', !hasText)
    }
  }

  _mainImageCount() {
    if (!this.element) return 0
    // If there is any image with data-gallery-url inside the main area, treat it as the leading main image.
    const mainEl = this.element.querySelector("[data-hero-layout-target='main'] [data-gallery-url]")
    return mainEl ? 1 : 0
  }
}
